<div class="p-2">
    <div class="flex justify-between items-center mb-4">
        <div>
            <h2 i18n>Base Role Management</h2>
            <p class="purple-sub-title" i18n>Manage system base roles and their permissions. Only accessible to SUPER_DEV_ADMIN users.</p>
        </div>
        @if (!canAccessBaseRoles()) {
        <div class="text-red-500 text-sm">
            <i class="pi pi-exclamation-triangle mr-1"></i>
            <span i18n>Access Restricted to SUPER_DEV_ADMIN</span>
        </div>
        }
    </div>

    @if (canAccessBaseRoles()) {
    <!-- Base Roles Table -->
    <div class="card">
        <div class="flex justify-between items-center mb-4">
            <h3 i18n>System Base Roles</h3>
            <div class="text-sm text-gray-600">
                <i class="pi pi-info-circle mr-1"></i>
                <span i18n>These roles form the foundation of the permission system</span>
                @if (permissionsLoaded) {
                    <span class="ml-2" i18n>({{ permissions.length }} permissions loaded)</span>
                } @else {
                    <span class="ml-2 text-orange-600" i18n>(Loading permissions...)</span>
                }
            </div>
        </div>

        <p-table [value]="baseRoles" [tableStyle]="{'min-width': '50rem'}" [loading]="loading" [showLoader]="true">
            <ng-template pTemplate="header">
                <tr>
                    <th i18n>ROLE NAME</th>
                    <th i18n>IDENTIFICATION</th>
                    <th i18n>DESCRIPTION</th>
                    <th i18n>PERMISSIONS</th>
                    <th i18n>USERS</th>
                    <th i18n>ACTIONS</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-role>
                <tr>
                    <td>
                        <div class="font-medium">{{ role.name }}</div>
                    </td>
                    <td>
                        <p-tag [value]="role.identification" [severity]="getRoleSeverity(role.identification)"></p-tag>
                    </td>
                    <td>
                        <div class="text-sm text-gray-600 max-w-xs">
                            {{ getRoleDescription(role.identification) }}
                        </div>
                    </td>
                    <td>
                        <div class="text-sm">
                            <span class="font-medium">{{ role.permissions.length || 0 }}</span> <span i18n>permissions</span>
                        </div>
                    </td>
                    <td>
                        <div class="text-sm">{{ role.userCount || 0 }} <span i18n>users</span></div>
                    </td>
                    <td>
                        <button 
                            pButton 
                            [icon]="permissionsLoaded ? 'pi pi-pencil' : 'pi pi-spin pi-spinner'" 
                            (click)="openDrawer(role)" 
                            class="p-button-text p-button-sm"
                            [disabled]="!permissionsLoaded"
                            [pTooltip]="permissionsLoaded ? 'Edit Base Role' : 'Loading permissions...'" i18n-pTooltip
                            tooltipPosition="top">
                        </button>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="6" class="text-center py-4">
                        <div class="flex flex-col items-center justify-center space-y-2">
                            <i class="pi pi-inbox text-3xl text-gray-400"></i>
                            <span class="text-gray-500" i18n>No base roles found</span>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>

    <!-- Info Section -->
    <div class="card mt-4">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-start">
                <i class="pi pi-info-circle text-blue-500 mr-3 mt-1"></i>
                <div>
                    <h4 class="text-blue-800 font-medium mb-2" i18n>About Base Roles</h4>
                    <div class="text-blue-700 text-sm space-y-1">
                        <p i18n>• Base roles are fundamental system roles that cannot be deleted</p>
                        <p i18n>• You can modify their names and permission assignments</p>
                        <p i18n>• Changes to base roles affect all subscribers using these roles</p>
                        <p i18n>• Use caution when modifying permissions as it impacts system security</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
    }

    @if (!canAccessBaseRoles()) {
    <!-- Access Denied Message -->
    <div class="card">
        <div class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <i class="pi pi-lock text-red-500 text-4xl mb-4"></i>
            <h3 class="text-red-800 font-medium mb-2" i18n>Access Restricted</h3>
            <p class="text-red-700" i18n>This area is only accessible to SUPER_DEV_ADMIN users. Base role management requires the highest level of system privileges.</p>
        </div>
    </div>
    }
</div>

<!-- Right Side Drawer for Editing -->
<p-drawer [(visible)]="drawerVisible" header="Edit Base Role" position="right" [baseZIndex]="10000" [modal]="true" styleClass="!w-full md:!w-[30rem] lg:!w-[60rem]">
    <ng-container *ngIf="selectedRole">
        <form [formGroup]="roleForm" (ngSubmit)="onSubmit()">
            <div class="mb-4">
                <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <div class="flex items-center">
                        <i class="pi pi-exclamation-triangle text-yellow-600 mr-2"></i>
                        <span class="text-yellow-800 text-sm font-medium">Warning: Modifying base roles affects all system users</span>
                    </div>
                </div>
            </div>

            <div class="mb-4">
                <label class="required" i18n>Role Name *</label>
                <input pInputText type="text" class="w-full" formControlName="name" />
                <div *ngIf="roleForm.get('name')?.invalid && roleForm.get('name')?.touched" class="text-red-500 text-sm mt-1">
                    <div *ngIf="roleForm.get('name')?.errors?.['required']" i18n>Role name is required</div>
                    <div *ngIf="roleForm.get('name')?.errors?.['minlength']" i18n>Role name must be at least 3 characters</div>
                </div>
            </div>

            <div class="mb-4">
                <label i18n>Role Identification</label>
                <input pInputText type="text" class="w-full" formControlName="identification" readonly />
                <div class="text-sm text-gray-600 mt-1" i18n>This field cannot be modified for base roles</div>
            </div>

            <div class="mb-4">
                <div class="role-info p-3 bg-gray-50 rounded">
                    <div class="text-sm font-medium mb-2" i18n>Current Role Information</div>
                    <div class="text-sm text-gray-600 space-y-1">
                        <div>Users with this role: <span class="font-medium">{{ selectedRole.userCount || 0 }}</span></div>
                        <div>Current permissions: <span class="font-medium">{{ selectedRole.permissions.length || 0 }}</span></div>
                    </div>
                </div>
            </div>

            <div class="mb-4">
                <label class="font-medium mb-2 block" i18n>Permissions</label>
                <p class="text-sm text-gray-600 mb-3" i18n>Select the permissions this base role should have. These will be inherited by all users with this role.</p>
                
                <p-accordion>
                    <p-accordion-panel *ngFor="let category of permissionCategories; let i = index" [value]="i">
                        <p-accordion-header>
                            <div class="flex justify-between items-center w-full">
                                <span class="font-medium">{{ category }}</span>
                                <span class="text-sm text-gray-500 mr-3">
                                    {{ getSelectedPermissionsInCategory(category) }} / {{ permissionsByCategory[category].length || 0 }}
                                </span>
                            </div>
                        </p-accordion-header>
                        <p-accordion-content>
                            <div class="space-y-2">
                                <div *ngFor="let perm of permissionsByCategory[category]" class="flex items-center">
                                    <p-toggleSwitch [formControl]="getPermissionControl(perm._id)" class="mr-2">
                                    </p-toggleSwitch>
                                    <div class="flex-1">
                                        <span class="text-sm font-medium">{{ getPermissionDisplayName(perm) }}</span>
                                        <div class="text-xs text-gray-500">{{ perm.category || 'No category' }}</div>
                                    </div>
                                </div>
                            </div>
                        </p-accordion-content>
                    </p-accordion-panel>
                </p-accordion>
            </div>

            <div class="flex gap-2 mt-6">
                <button pButton label="Cancel" class="p-button-outlined w-half" (click)="closeDrawer()" type="button"></button>
                <button pButton label="Update Role" class="w-half" type="submit" [disabled]="roleForm.invalid">
                    <i class="pi pi-save mr-2"></i>
                </button>
            </div>
        </form>
    </ng-container>
</p-drawer>

<!-- Toast for messages -->
<p-toast></p-toast>

<!-- Confirmation Dialog -->
<p-confirmDialog></p-confirmDialog>
